import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, Checkbox } from '../ui';
import { DynamicTable, Column } from './DynamicTable';
import { maskCurrency, unmaskCurrency } from '../../utils/masks';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import type {
  DebtData,
  SupplierDebt,
  TradingDebt,
  BankDebt,
  CreditLimit,
  ThirdPartyDebt,
} from '../../types';

// ============================================
// Factory functions para criação de novas linhas
// ============================================

const createNewSupplierDebt = (): SupplierDebt => ({
  id: `supplier-${Date.now()}`,
  company: '',
  valueUsd: 0,
  payment2024: 0,
  remainingBalance: 0,
});

const createNewTradingDebt = (): TradingDebt => ({
  id: `trading-${Date.now()}`,
  trading: '',
  valueUsd: 0,
  payment2024: 0,
  remainingBalance: 0,
});

const createNewBankDebt = (): BankDebt => ({
  id: `bank-${Date.now()}`,
  bank: '',
  modality: '',
  currency: 'BRL',
  startDate: '',
  dueDate: '',
  initialValue: 0,
  currentBalance: 0,
  payment2024: 0,
  guarantees: '',
});

const createNewCreditLimit = (): CreditLimit => ({
  id: `credit-${Date.now()}`,
  bank: '',
  approvedLimit: 0,
  currency: 'BRL',
  updatedValue: 0,
  usage2024: 0,
  usage2025: 0,
  usage2026: 0,
});

const createNewThirdPartyDebt = (): ThirdPartyDebt => ({
  id: `third-${Date.now()}`,
  personOrCompany: '',
  relationship: '',
  currency: 'BRL',
  value: 0,
  dueDate: '',
  guarantee: '',
});

// ============================================
// Factory de colunas genérico para dívidas com formato simples
// (empresa/trading, valor, pagamento 2024, saldo)
// ============================================

interface SimpleDebtItem {
  id: string;
  valueUsd: number;
  payment2024: number;
  remainingBalance: number;
}

function createDebtColumns<T extends SimpleDebtItem>(
  entityKey: keyof T,
  entityHeader: string,
  entityPlaceholder: string,
  disabled: boolean,
  labels?: { valueUsd: string; payment2024: string; balance: string }
): Column<T>[] {
  return [
    {
      key: entityKey as string,
      header: entityHeader,
      width: '30%',
      render: (item, _index, onChange) => (
        <Input
          value={(item[entityKey] as string) || ''}
          onChange={(value) => onChange({ ...item, [entityKey]: value })}
          placeholder={entityPlaceholder}
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'valueUsd',
      header: labels?.valueUsd ?? 'Valor (USD)',
      width: '25%',
      render: (item, _index, onChange) => (
        <Input
          value={item.valueUsd ? maskCurrency(item.valueUsd, 'USD') : ''}
          onChange={(value) => {
            const newValue = unmaskCurrency(value);
            onChange({
              ...item,
              valueUsd: newValue,
              remainingBalance: newValue - item.payment2024,
            });
          }}
          placeholder="$ 0.00"
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'payment2024',
      header: labels?.payment2024 ?? 'Pagamento 2024',
      width: '25%',
      render: (item, _index, onChange) => (
        <Input
          value={item.payment2024 ? maskCurrency(item.payment2024, 'USD') : ''}
          onChange={(value) => {
            const newPayment = unmaskCurrency(value);
            onChange({
              ...item,
              payment2024: newPayment,
              remainingBalance: item.valueUsd - newPayment,
            });
          }}
          placeholder="$ 0.00"
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'remainingBalance',
      header: labels?.balance ?? 'Saldo',
      width: '20%',
      render: (item) => (
        <span className="text-sm font-medium text-gray-700">
          {maskCurrency(item.remainingBalance || 0, 'USD')}
        </span>
      ),
    },
  ];
}

export interface DebtSectionProps {
  data: Partial<DebtData>;
  onSupplierDebtsChange: (debts: SupplierDebt[]) => void;
  onTradingDebtsChange: (debts: TradingDebt[]) => void;
  onBankDebtsChange: (debts: BankDebt[]) => void;
  onCreditLimitsChange: (limits: CreditLimit[]) => void;
  onThirdPartyDebtsChange: (debts: ThirdPartyDebt[]) => void;
  naFields: Record<string, boolean>;
  onNaChange: (key: string, value: boolean) => void;
  disabled?: boolean;
}

export const DebtSection: React.FC<DebtSectionProps> = ({
  data,
  onSupplierDebtsChange,
  onTradingDebtsChange,
  onBankDebtsChange,
  onCreditLimitsChange,
  onThirdPartyDebtsChange,
  naFields,
  onNaChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // Colunas de dívidas com fornecedores
  const supplierDebtColumns = useMemo(
    () => createDebtColumns<SupplierDebt>('company', t('debt.suppliers.company'), t('debt.suppliers.company'), disabled, { valueUsd: t('debt.valueUsd'), payment2024: t('debt.payment2024'), balance: t('debt.balance') }),
    [disabled, t]
  );

  // Colunas de dívidas com tradings
  const tradingDebtColumns = useMemo(
    () => createDebtColumns<TradingDebt>('trading', t('debt.tradings.trading'), t('debt.tradings.trading'), disabled, { valueUsd: t('debt.valueUsd'), payment2024: t('debt.payment2024'), balance: t('debt.balance') }),
    [disabled, t]
  );

  // Colunas de dívidas bancárias
  const bankDebtColumns: Column<BankDebt>[] = useMemo(
    () => [
      {
        key: 'bank',
        header: t('debt.banks.bank'),
        render: (item, _index, onChange) => (
          <Input
            value={item.bank}
            onChange={(value) => onChange({ ...item, bank: value })}
            placeholder="Nome do banco"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'modality',
        header: t('debt.banks.modality'),
        render: (item, _index, onChange) => (
          <Input
            value={item.modality}
            onChange={(value) => onChange({ ...item, modality: value })}
            placeholder="Ex: Crédito Rural"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'currency',
        header: t('debt.banks.currency'),
        render: (item, _index, onChange) => (
          <Select
            value={item.currency}
            onChange={(value) => onChange({ ...item, currency: value as 'BRL' | 'USD' })}
            options={CURRENCY_OPTIONS}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'initialValue',
        header: t('debt.banks.initialValue'),
        render: (item, _index, onChange) => (
          <Input
            value={item.initialValue ? maskCurrency(item.initialValue, item.currency) : ''}
            onChange={(value) => onChange({ ...item, initialValue: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'currentBalance',
        header: t('debt.banks.currentBalance'),
        render: (item, _index, onChange) => (
          <Input
            value={item.currentBalance ? maskCurrency(item.currentBalance, item.currency) : ''}
            onChange={(value) => onChange({ ...item, currentBalance: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'guarantees',
        header: t('debt.banks.guarantees'),
        render: (item, _index, onChange) => (
          <Input
            value={item.guarantees}
            onChange={(value) => onChange({ ...item, guarantees: value })}
            placeholder="Garantias"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  // Colunas de limites de crédito
  const creditLimitColumns: Column<CreditLimit>[] = useMemo(
    () => [
      {
        key: 'bank',
        header: t('debt.banks.bank'),
        render: (item, _index, onChange) => (
          <Input
            value={item.bank}
            onChange={(value) => onChange({ ...item, bank: value })}
            placeholder="Nome do banco"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'currency',
        header: t('debt.banks.currency'),
        render: (item, _index, onChange) => (
          <Select
            value={item.currency}
            onChange={(value) => onChange({ ...item, currency: value as 'BRL' | 'USD' })}
            options={CURRENCY_OPTIONS}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'approvedLimit',
        header: t('debt.creditLimits.approvedLimit'),
        render: (item, _index, onChange) => (
          <Input
            value={item.approvedLimit ? maskCurrency(item.approvedLimit, item.currency) : ''}
            onChange={(value) => onChange({ ...item, approvedLimit: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'updatedValue',
        header: t('debt.creditLimits.updatedValue'),
        render: (item, _index, onChange) => (
          <Input
            value={item.updatedValue ? maskCurrency(item.updatedValue, item.currency) : ''}
            onChange={(value) => onChange({ ...item, updatedValue: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'usage2024',
        header: t('debt.usage2024'),
        render: (item, _index, onChange) => (
          <Input
            value={item.usage2024 ? maskCurrency(item.usage2024, item.currency) : ''}
            onChange={(value) => onChange({ ...item, usage2024: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'usage2025',
        header: t('debt.usage2025'),
        render: (item, _index, onChange) => (
          <Input
            value={item.usage2025 ? maskCurrency(item.usage2025, item.currency) : ''}
            onChange={(value) => onChange({ ...item, usage2025: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'usage2026',
        header: t('debt.usage2026'),
        render: (item, _index, onChange) => (
          <Input
            value={item.usage2026 ? maskCurrency(item.usage2026, item.currency) : ''}
            onChange={(value) => onChange({ ...item, usage2026: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  // Colunas de dívidas com terceiros
  const thirdPartyDebtColumns: Column<ThirdPartyDebt>[] = useMemo(
    () => [
      {
        key: 'personOrCompany',
        header: t('debt.thirdParty.personOrCompany'),
        render: (item, _index, onChange) => (
          <Input
            value={item.personOrCompany}
            onChange={(value) => onChange({ ...item, personOrCompany: value })}
            placeholder="Nome"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'relationship',
        header: t('debt.thirdParty.relationship'),
        render: (item, _index, onChange) => (
          <Input
            value={item.relationship}
            onChange={(value) => onChange({ ...item, relationship: value })}
            placeholder="Ex: Sócio, Familiar"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'currency',
        header: t('debt.banks.currency'),
        render: (item, _index, onChange) => (
          <Select
            value={item.currency}
            onChange={(value) => onChange({ ...item, currency: value as 'BRL' | 'USD' })}
            options={CURRENCY_OPTIONS}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'value',
        header: t('debt.thirdParty.value'),
        render: (item, _index, onChange) => (
          <Input
            value={item.value ? maskCurrency(item.value, item.currency) : ''}
            onChange={(value) => onChange({ ...item, value: unmaskCurrency(value) })}
            placeholder="0,00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'dueDate',
        header: t('debt.thirdParty.dueDate'),
        render: (item, _index, onChange) => (
          <Input
            value={item.dueDate}
            onChange={(value) => onChange({ ...item, dueDate: value })}
            placeholder="DD/MM/AAAA"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'guarantee',
        header: t('debt.thirdParty.guarantee'),
        render: (item, _index, onChange) => (
          <Input
            value={item.guarantee}
            onChange={(value) => onChange({ ...item, guarantee: value })}
            placeholder="Garantia"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  // Flags N/A de cada subseção
  const isSupplierNa = naFields['debt.supplier'] ?? false;
  const isTradingNa = naFields['debt.trading'] ?? false;
  const isBankNa = naFields['debt.bank'] ?? false;
  const isCreditNa = naFields['debt.credit'] ?? false;
  const isThirdPartyNa = naFields['debt.thirdParty'] ?? false;

  return (
    <div className="space-y-8">
      {/* a. Endividamento com Fornecedores */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-sm font-medium text-gray-700">
              {t('debt.suppliers.title')}
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              {t('debt.suppliers.description')}
            </p>
          </div>
          <Checkbox
            label={t('common.na')}
            checked={isSupplierNa}
            onChange={(checked) => onNaChange('debt.supplier', checked)}
            disabled={disabled}
          />
        </div>

        {!isSupplierNa && (
          <DynamicTable
            columns={supplierDebtColumns}
            data={data.supplierDebts || []}
            onChange={onSupplierDebtsChange}
            createNewItem={createNewSupplierDebt}
            addButtonText={t('debt.suppliers.addButton')}
            disabled={disabled}
          />
        )}
      </div>

      {/* b. Endividamento com Tradings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">{t('debt.tradings.title')}</h4>
          <Checkbox
            label={t('common.na')}
            checked={isTradingNa}
            onChange={(checked) => onNaChange('debt.trading', checked)}
            disabled={disabled}
          />
        </div>

        {!isTradingNa && (
          <DynamicTable
            columns={tradingDebtColumns}
            data={data.tradingDebts || []}
            onChange={onTradingDebtsChange}
            createNewItem={createNewTradingDebt}
            addButtonText={t('debt.tradings.addButton')}
            disabled={disabled}
          />
        )}
      </div>

      {/* c. Endividamento Bancário */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">{t('debt.banks.title')}</h4>
          <Checkbox
            label={t('common.na')}
            checked={isBankNa}
            onChange={(checked) => onNaChange('debt.bank', checked)}
            disabled={disabled}
          />
        </div>

        {!isBankNa && (
          <DynamicTable
            columns={bankDebtColumns}
            data={data.bankDebts || []}
            onChange={onBankDebtsChange}
            createNewItem={createNewBankDebt}
            addButtonText={t('debt.banks.addButton')}
            disabled={disabled}
          />
        )}
      </div>

      {/* d. Limites de Crédito */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">{t('debt.creditLimits.title')}</h4>
          <Checkbox
            label={t('common.na')}
            checked={isCreditNa}
            onChange={(checked) => onNaChange('debt.credit', checked)}
            disabled={disabled}
          />
        </div>

        {!isCreditNa && (
          <DynamicTable
            columns={creditLimitColumns}
            data={data.creditLimits || []}
            onChange={onCreditLimitsChange}
            createNewItem={createNewCreditLimit}
            addButtonText={t('debt.creditLimits.addButton')}
            disabled={disabled}
          />
        )}
      </div>

      {/* e. Endividamento com Terceiros */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">{t('debt.thirdParty.title')}</h4>
          <Checkbox
            label={t('common.na')}
            checked={isThirdPartyNa}
            onChange={(checked) => onNaChange('debt.thirdParty', checked)}
            disabled={disabled}
          />
        </div>

        {!isThirdPartyNa && (
          <DynamicTable
            columns={thirdPartyDebtColumns}
            data={data.thirdPartyDebts || []}
            onChange={onThirdPartyDebtsChange}
            createNewItem={createNewThirdPartyDebt}
            addButtonText={t('debt.thirdParty.addButton')}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export default DebtSection;
